import React, { useEffect, useRef, useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface VoiceTextareaProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
}

export default function VoiceTextarea({
  value,
  onChange,
  rows = 4,
  className = "form-input",
}: VoiceTextareaProps) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const startingValueRef = useRef("");
  const finalTranscriptRef = useRef("");
  const [recording, setRecording] = useState(false);
  const Recognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;

  useEffect(() => () => recognitionRef.current?.abort(), []);

  const stop = () => recognitionRef.current?.stop();

  const start = () => {
    if (!Recognition || recording) return;

    const recognition = new Recognition();
    startingValueRef.current = value.trimEnd();
    finalTranscriptRef.current = "";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-GB";
    recognition.onresult = (event) => {
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index][0].transcript;
        if (event.results[index].isFinal) finalTranscriptRef.current += transcript;
        else interim += transcript;
      }

      const spokenText = `${finalTranscriptRef.current}${interim}`.trim();
      const separator = startingValueRef.current && spokenText ? " " : "";
      onChange(`${startingValueRef.current}${separator}${spokenText}`);
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setRecording(false);
    };
    recognition.onerror = () => setRecording(false);
    recognitionRef.current = recognition;
    setRecording(true);

    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setRecording(false);
    }
  };

  return (
    <div className={`voice-textarea ${recording ? "voice-textarea--recording" : ""}`}>
      <textarea
        className={className}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {Recognition && (
        <button
          type="button"
          className="voice-textarea-button"
          onClick={recording ? stop : start}
          title={recording ? "Stop dictation" : "Dictate this field"}
          aria-label={recording ? "Stop dictation" : "Start voice dictation"}
          aria-pressed={recording}
        >
          {recording ? <StopCircleOutlinedIcon /> : <MicIcon />}
        </button>
      )}
      {recording && <span className="voice-textarea-status" role="status">Listening…</span>}
    </div>
  );
}
